import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  AgentEventsEnum,
  LiveAvatarSession,
  SessionDisconnectReason,
  SessionEvent,
  SessionState,
  VoiceChatEvent,
} from "@heygen/liveavatar-web-sdk";

import LiveAvatarService from "../services/liveAvatar.service";
import type {
  LiveAvatarError,
  LiveAvatarErrorCode,
  LiveAvatarStatus,
  LiveAvatarTranscriptMessage,
} from "../components/live-avatar/types";

export interface UseLiveAvatarOptions {
  eventId: number;
  realtimeBookingId: number;
  timezone: string;
  mediaRef: React.RefObject<HTMLVideoElement>;
  autoStart?: boolean;
  debug?: boolean;
  onTranscript?: (message: LiveAvatarTranscriptMessage) => void;
  onStatusChange?: (status: LiveAvatarStatus) => void;
}

export interface UseLiveAvatarResult {
  status: LiveAvatarStatus;
  error: LiveAvatarError | null;
  isMuted: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  toggleMute: () => Promise<void>;
}

const buildError = (
  code: LiveAvatarErrorCode,
  message: string,
  cause?: unknown,
): LiveAvatarError => ({ code, message, cause });

const mapState = (state: SessionState): LiveAvatarStatus => {
  switch (state) {
    case SessionState.CONNECTING:
      return "connecting";
    case SessionState.CONNECTED:
      return "connected";
    case SessionState.DISCONNECTING:
      return "disconnecting";
    case SessionState.DISCONNECTED:
    case SessionState.INACTIVE:
    default:
      return "idle";
  }
};

const debugLogFactory =
  (enabled: boolean) =>
  (...args: unknown[]) => {
    if (!enabled) return;
    const ts = new Date().toISOString();
    // eslint-disable-next-line no-console
    console.log(`[LiveAvatar][${ts}]`, ...args);
  };

export default function useLiveAvatar(
  options: UseLiveAvatarOptions,
): UseLiveAvatarResult {
  const {
    eventId,
    realtimeBookingId,
    timezone,
    mediaRef,
    autoStart = false,
    debug = false,
    onTranscript,
    onStatusChange,
  } = options;

  const [status, setStatus] = useState<LiveAvatarStatus>("idle");
  const [error, setError] = useState<LiveAvatarError | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const sessionRef = useRef<LiveAvatarSession | null>(null);
  const isMountedRef = useRef(true);
  const statusRef = useRef<LiveAvatarStatus>("idle");
  const logRef = useRef(debugLogFactory(debug));

  useEffect(() => {
    logRef.current = debugLogFactory(debug);
  }, [debug]);

  const updateStatus = useCallback(
    (next: LiveAvatarStatus) => {
      if (!isMountedRef.current) return;
      statusRef.current = next;
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange],
  );

  const teardown = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;

    const video = mediaRef.current;
    if (video) {
      video.pause();
      if (video.srcObject instanceof MediaStream) {
        video.srcObject.getTracks().forEach((t) => t.stop());
      }
      video.srcObject = null;
    }
    setIsMuted(false);

    if (session) {
      try {
        session.removeAllListeners();
      } catch {
        /* ignore */
      }
      try {
        session.voiceChat.removeAllListeners();
      } catch {
        /* ignore */
      }
      try {
        await session.stop();
      } catch (err) {
        logRef.current("stop error", err);
      }
    }
  }, [mediaRef]);

  const stop = useCallback(async () => {
    if (status === "idle") return;
    updateStatus("disconnecting");
    await teardown();
    updateStatus("idle");
  }, [status, teardown, updateStatus]);

  const start = useCallback(async () => {
    if (sessionRef.current || status === "connecting" || status === "connected") {
      return;
    }

    if (!eventId || !realtimeBookingId || !timezone) {
      const err = buildError(
        "config_missing",
        "LiveAvatar realtime session context is missing.",
      );
      setError(err);
      updateStatus("error");
      return;
    }

    setError(null);
    setIsMuted(false);
    updateStatus("connecting");
    logRef.current("start", {
      eventId,
      realtimeBookingId,
      timezone,
    });

    try {
      const sessionResponse = await LiveAvatarService.createSession({
        event_id: eventId,
        realtime_booking_id: realtimeBookingId,
        timezone,
      });

      if (!isMountedRef.current || statusRef.current !== "connecting") return;

      const sessionToken = sessionResponse?.sessionToken;
      if (!sessionToken) {
        throw buildError(
          "session",
          "Backend did not return a LiveAvatar session token.",
        );
      }

      const session = new LiveAvatarSession(sessionToken, { voiceChat: true });
      sessionRef.current = session;

      session.voiceChat.on(VoiceChatEvent.MUTED, () => {
        logRef.current("voice_chat.muted");
        if (isMountedRef.current) setIsMuted(true);
      });

      session.voiceChat.on(VoiceChatEvent.UNMUTED, () => {
        logRef.current("voice_chat.unmuted");
        if (isMountedRef.current) setIsMuted(false);
      });

      session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
        logRef.current("session.state_changed", state);
        const mapped = mapState(state);
        // Don't overwrite "error" here; the stop/teardown paths handle idle.
        if (mapped !== "idle") updateStatus(mapped);
      });

      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        logRef.current("session.stream_ready");
        const video = mediaRef.current;
        if (!video) return;
        video.autoplay = true;
        video.playsInline = true;
        session.attach(video);
        void video.play().catch(() => undefined);
      });

      session.on(SessionEvent.SESSION_DISCONNECTED, (reason) => {
        logRef.current("session.disconnected", reason);
        sessionRef.current = null;
        if (!isMountedRef.current) return;
        if (reason === SessionDisconnectReason.SESSION_START_FAILED) {
          setError(buildError("session", "Avatar session failed to start."));
          updateStatus("error");
        } else {
          setIsMuted(false);
          updateStatus("idle");
        }
      });

      session.on(AgentEventsEnum.USER_TRANSCRIPTION, (event) => {
        if (!event?.text) return;
        onTranscript?.({
          role: "user",
          text: event.text,
          eventId: event.event_id,
        });
      });

      session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (event) => {
        if (!event?.text) return;
        onTranscript?.({
          role: "agent",
          text: event.text,
          eventId: event.event_id,
        });
      });

      await session.start();
      if (isMountedRef.current) {
        setIsMuted(session.voiceChat.isMuted);
      }

      if (!isMountedRef.current) {
        await teardown();
      }
    } catch (err) {
      logRef.current("start error", err);
      await teardown();
      if (!isMountedRef.current) return;

      const normalized: LiveAvatarError =
        err && typeof err === "object" && "code" in (err as object)
          ? (err as LiveAvatarError)
          : err instanceof DOMException && err.name === "NotAllowedError"
            ? buildError(
                "permission_denied",
                "Microphone permission was denied.",
                err,
              )
            : buildError(
                "unknown",
                err instanceof Error
                  ? err.message
                  : "Failed to start avatar session.",
                err,
              );

      setError(normalized);
      updateStatus("error");
      Sentry.captureException(err, {
        tags: { feature: "liveavatar" },
        extra: {
          eventId,
          realtimeBookingId,
          code: normalized.code,
        },
      });
    }
  }, [
    eventId,
    realtimeBookingId,
    timezone,
    mediaRef,
    onTranscript,
    status,
    teardown,
    updateStatus,
  ]);

  const toggleMute = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || status !== "connected") return;

    try {
      if (session.voiceChat.isMuted) {
        await session.voiceChat.unmute();
        if (isMountedRef.current) setIsMuted(false);
      } else {
        await session.voiceChat.mute();
        if (isMountedRef.current) setIsMuted(true);
      }
    } catch (err) {
      logRef.current("toggle mute error", err);
    }
  }, [status]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      void teardown();
    };
  }, [teardown]);

  const didAutoStartRef = useRef(false);
  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    if (!eventId || !realtimeBookingId || !timezone) return;
    didAutoStartRef.current = true;
    void start();
  }, [autoStart, eventId, realtimeBookingId, start, timezone]);

  return { status, error, isMuted, start, stop, toggleMute };
}
